if (!customElements.get('sticky-product-bar')) {
  customElements.define(
    'sticky-product-bar',
    class StickyProductBar extends HTMLElement {
      constructor() {
        super();

        this.onScroll = this.onScroll.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onPickerKeydown = this.onPickerKeydown.bind(this);
        this.onPickerToggle = this.onPickerToggle.bind(this);
        this.onFormError = this.onFormError.bind(this);
        this.onSubmissionEnd = this.onSubmissionEnd.bind(this);
        this.syncFromNativeForm = this.syncFromNativeForm.bind(this);
      }

      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.nativeForm = document.getElementById(`product-form-${this.sectionId}`);
        this.productForm = this.nativeForm?.closest('product-form');
        this.mobileViewport = window.matchMedia('(max-width: 53.125rem)');
        this.nativeButton = this.nativeForm?.querySelector('[name="add"]');
        this.nativeVariantInput = this.nativeForm?.querySelector('input[name="id"]');
        this.nativeVariantSelector = document.querySelector(
          `variant-selects[data-section="${this.sectionId}"]`,
        );
        this.variantPicker = this.querySelector('[data-sticky-variant-picker]');
        this.variantToggle = this.querySelector('[data-sticky-variant-toggle]');
        this.variantLabel = this.querySelector('[data-sticky-variant-label]');
        this.variantOptions = Array.from(
          this.querySelectorAll('[data-sticky-product-variant-option]'),
        );
        this.submitButton = this.querySelector('[data-sticky-product-submit]');
        this.submitText = this.querySelector('[data-sticky-product-submit-text]');
        this.price = this.querySelector('[data-sticky-product-price]');

        if (!this.nativeButton || !this.submitButton) return;

        this.variantPicker?.addEventListener('click', this.onVariantChange);
        this.variantPicker?.addEventListener('keydown', this.onPickerKeydown);
        this.variantPicker?.addEventListener('toggle', this.onPickerToggle);
        this.submitButton.addEventListener('click', this.onSubmit);
        this.productForm?.addEventListener('product-form:error', this.onFormError);
        this.productForm?.addEventListener('product-form:submission-end', this.onSubmissionEnd);
        this.nativeVariantInput?.addEventListener('change', this.syncFromNativeForm);
        document.addEventListener('click', this.onDocumentClick);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });

        this.nativeButtonObserver = new MutationObserver(this.syncFromNativeForm);
        this.nativeButtonObserver.observe(this.nativeButton, {
          attributes: true,
          attributeFilter: ['aria-busy', 'disabled', 'class'],
          childList: true,
          subtree: true,
        });

        this.syncFromNativeForm();
        this.updateVisibility();
      }

      disconnectedCallback() {
        this.variantPicker?.removeEventListener('click', this.onVariantChange);
        this.variantPicker?.removeEventListener('keydown', this.onPickerKeydown);
        this.variantPicker?.removeEventListener('toggle', this.onPickerToggle);
        this.submitButton?.removeEventListener('click', this.onSubmit);
        this.productForm?.removeEventListener('product-form:error', this.onFormError);
        this.productForm?.removeEventListener('product-form:submission-end', this.onSubmissionEnd);
        this.nativeVariantInput?.removeEventListener('change', this.syncFromNativeForm);
        document.removeEventListener('click', this.onDocumentClick);
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        this.nativeButtonObserver?.disconnect();

        if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
        if (this.errorScrollFrame) cancelAnimationFrame(this.errorScrollFrame);
        this.pendingSubmission = false;
      }

      onScroll() {
        if (this.scrollFrame) return;

        this.scrollFrame = requestAnimationFrame(() => {
          this.updateVisibility();
          this.scrollFrame = null;
        });
      }

      updateVisibility() {
        const buttonRect = this.nativeButton.getBoundingClientRect();
        const buttonIsRendered = this.nativeButton.getClientRects().length > 0;
        const hasPassedNativeButton = buttonIsRendered && buttonRect.bottom <= 0;
        const isVisible = this.mobileViewport.matches || hasPassedNativeButton;

        this.dataset.visible = isVisible ? 'true' : 'false';
        this.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
        this.toggleAttribute('inert', !isVisible);
      }

      onVariantChange(event) {
        const stickyVariantOption = event.target.closest(
          '[data-sticky-product-variant-option]',
        );
        if (!stickyVariantOption || stickyVariantOption.disabled) return;

        const variantId = stickyVariantOption.dataset.variantId;
        if (!variantId || !this.nativeVariantSelector) return;

        const nativeVariantButton = Array.from(
          this.nativeVariantSelector.querySelectorAll('.variant-block[data-variant-id]'),
        ).find((button) => button.dataset.variantId === variantId);

        if (!nativeVariantButton) return;

        nativeVariantButton.click();
        this.variantPicker.removeAttribute('open');
        this.variantToggle.focus({ preventScroll: true });
        this.syncFromNativeForm();
      }

      onDocumentClick(event) {
        if (this.variantPicker?.open && !this.variantPicker.contains(event.target)) {
          this.variantPicker.removeAttribute('open');
        }
      }

      onPickerToggle() {
        this.variantToggle?.setAttribute(
          'aria-expanded',
          this.variantPicker.open ? 'true' : 'false',
        );
      }

      onPickerKeydown(event) {
        if (event.key === 'Escape' && this.variantPicker.open) {
          event.preventDefault();
          this.variantPicker.removeAttribute('open');
          this.variantToggle.focus({ preventScroll: true });
          return;
        }

        if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;

        event.preventDefault();
        if (!this.variantPicker.open) this.variantPicker.setAttribute('open', '');

        const enabledOptions = this.variantOptions.filter((option) => !option.disabled);
        const activeIndex = enabledOptions.indexOf(document.activeElement);
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        const nextIndex =
          activeIndex < 0
            ? direction > 0
              ? 0
              : enabledOptions.length - 1
            : (activeIndex + direction + enabledOptions.length) % enabledOptions.length;

        enabledOptions[nextIndex]?.focus({ preventScroll: true });
      }

      onSubmit() {
        if (this.submitButton.disabled || this.nativeButton.disabled) return;

        this.pendingSubmission = true;
        this.nativeButton.click();
        this.syncFromNativeForm();
      }

      onFormError(event) {
        if (!this.pendingSubmission) return;
        this.pendingSubmission = false;
        this.variantPicker?.removeAttribute('open');

        const errorTarget = event.detail.target;
        if (!errorTarget) return;

        this.errorScrollFrame = requestAnimationFrame(() => {
          this.errorScrollFrame = null;
          if (!errorTarget.isConnected) return;

          if (!errorTarget.matches('input, select, textarea, button, a[href], [tabindex]')) {
            errorTarget.setAttribute('tabindex', '-1');
          }
          errorTarget.focus({ preventScroll: true });
          errorTarget.scrollIntoView({
            block: 'center',
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
          });
        });
      }

      onSubmissionEnd() {
        this.pendingSubmission = false;
      }

      syncFromNativeForm() {
        const variantId = this.nativeVariantInput?.value || '';
        const matchingOption = this.variantOptions.find(
          (option) => option.dataset.variantId === variantId,
        );

        this.variantOptions.forEach((option) => {
          const isSelected = option === matchingOption;
          option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        });

        if (this.variantLabel) {
          this.variantLabel.textContent =
            matchingOption?.dataset.variantTitle || this.variantPicker?.dataset.placeholder || '';
        }

        if (matchingOption?.dataset.price && this.price) {
          this.price.textContent = matchingOption.dataset.price;
        }

        const nativeText = this.nativeButton.querySelector(':scope > span')?.textContent.trim();
        if (nativeText && this.submitText) this.submitText.textContent = nativeText;

        const nativeAriaLabel = this.nativeButton.getAttribute('aria-label');
        if (nativeAriaLabel) this.submitButton.setAttribute('aria-label', nativeAriaLabel);

        const isBusy = this.nativeButton.getAttribute('aria-busy') === 'true';
        this.submitButton.disabled = this.nativeButton.disabled;
        this.submitButton.setAttribute('aria-busy', String(isBusy));
      }
    },
  );
}
